import { faShuffle } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioAvatarCard } from 'components/Form/RadioAvatarCard/RadioAvatarCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { StaffRadioCard } from 'components/Staff/RadioCard/StaffRadioCard';
import { Alert } from 'components/UI/Alert/Alert';
import { useMarketplace } from 'hooks/useMarketplace';
import { useTimeZone } from 'hooks/useTimeZone';
import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { getStaffs } from 'reducers/staffs/StaffsSelectors';
import { dispatch } from 'store';
import { Staff } from 'types/Staff';
import { fetchWalkingStaffAvailabilityThunk } from 'actions/onlineBooking/actions/OnlineBookingWalkingActions';
import {
    buildWalkingEligibilityRequestKey,
    buildWalkingStaffAvailabilityCalls,
    buildWalkingStaffAvailabilityRequestKey,
    leadTimeToWeeks
} from 'actions/onlineBooking/actions/OnlineBookingWalkingRequests';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingWalkingAddressId,
    getWalkingEligibleStaffIdsFromCache,
    ONLINE_BOOKING_SESSION_SELECTORS
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingStep, SessionStepProps } from 'types/OnlineBooking';
import {
    applyWalkingPreferredStaff,
    applyWalkingStaffOnSubmit,
    deriveWalkingStaffCards,
    deriveWalkingStaffStepView,
    SESSION_AREA_COPY,
    walkingStaffCardDescription,
    WalkingStaffAvailability,
    WALKING_STAFF_DATE_FORMAT
} from 'types/Walking';
import { getBusinessArea, trackStaffSelected } from 'utils/analytics';
import { NoAvailability } from 'components/OnlineBooking/Steps/SelectStaff/NoAvailability/NoAvailability';
import staffClasses from 'components/OnlineBooking/Grooming/StaffList/StaffSelector/StaffSelector.module.scss';

export const OnlineBookingWalkingStaffListStep: React.FC<SessionStepProps> = React.memo(props => {
    const { businessArea } = props;
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const sessionSelectors = ONLINE_BOOKING_SESSION_SELECTORS[businessArea];
    const copy = SESSION_AREA_COPY[businessArea];

    const staffs = useSelector(getStaffs);
    const walkingItems = useSelector(sessionSelectors.items);
    const preferredStaff = useSelector(sessionSelectors.preferredStaff);
    const eligibilityCache = useSelector(sessionSelectors.eligibility);
    const staffAvailabilityCache = useSelector(sessionSelectors.staffAvailability);
    const addressId = useSelector(getOnlineBookingWalkingAddressId);
    const allowSelectStaff = marketplace.onlineBookingPreferences.allowSelectStaff;

    const numberOfPets = walkingItems.length;
    const startDate = moment().tz(timeZone).format('YYYY-MM-DD');
    const weeks = leadTimeToWeeks(marketplace.onlineBookingPreferences.bookingDateLimits.to);
    const eligibilityKey = buildWalkingEligibilityRequestKey(
        marketplace.id,
        addressId,
        numberOfPets
    );

    const calls = React.useMemo(() => {
        if (!allowSelectStaff) {
            return [];
        }

        const knownStaffIds = staffs.map(staff => staff.id);

        return buildWalkingStaffAvailabilityCalls({
            items: walkingItems
                .filter(item => item.service?.id)
                .map(item => ({
                    serviceId: item.service!.id,
                    staffIds: getWalkingEligibleStaffIdsFromCache(
                        eligibilityCache,
                        eligibilityKey,
                        item.service!.id
                    )
                })),
            knownStaffIds,
            addressId,
            startDate,
            weeks
        });
    }, [
        allowSelectStaff,
        walkingItems,
        staffs,
        eligibilityCache,
        eligibilityKey,
        addressId,
        startDate,
        weeks
    ]);

    const staffIds = React.useMemo(() => calls[0]?.staffIds ?? [], [calls]);
    const availableStaffs = React.useMemo(
        () => staffs.filter(staff => staffIds.includes(staff.id)),
        [staffs, staffIds]
    );

    const cacheEntries = calls.map(call => {
        const requestKey = buildWalkingStaffAvailabilityRequestKey(
            marketplace.id,
            call.addressId,
            call.numberOfPets,
            call.serviceId,
            call.staffIds,
            call.startDate,
            call.weeks
        );

        return staffAvailabilityCache[requestKey];
    });

    const isLoading =
        allowSelectStaff &&
        staffIds.length > 0 &&
        cacheEntries.some(entry => !entry || entry.loading || (!entry.data && !entry.error));
    const fetchError = cacheEntries.find(entry => entry?.error)?.error;
    const responses: Array<WalkingStaffAvailability> = cacheEntries
        .map(entry => entry?.data)
        .filter((data): data is WalkingStaffAvailability => Boolean(data));
    const derivation = deriveWalkingStaffCards(
        staffIds,
        isLoading || fetchError || responses.length !== calls.length ? [] : responses
    );
    const reasonKey = responses.map(response => response.reason).join('|');

    React.useEffect(() => {
        if (!allowSelectStaff) {
            dispatch(navigateToStep(OnlineBookingStep.DatePicker));
        }
    }, [allowSelectStaff]);

    React.useEffect(() => {
        if ((calls[0]?.staffIds.length ?? 0) === 0) {
            return;
        }

        calls.forEach(call => {
            dispatch(
                fetchWalkingStaffAvailabilityThunk(
                    businessArea,
                    call.serviceId,
                    call.staffIds,
                    call.addressId,
                    call.numberOfPets,
                    call.startDate,
                    call.weeks
                )
            ).catch(() => undefined);
        });
    }, [businessArea, calls]);

    React.useEffect(() => {
        reasonKey.split('|').forEach(reason => {
            if (reason) {
                console.debug(`${copy.label} staff availability reason`, reason);
            }
        });
    }, [copy.label, reasonKey]);

    const backHandler = React.useCallback(() => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceList));
    }, []);

    const changePreferredStaffHandler = React.useCallback(
        (staff?: Staff) => {
            dispatch(
                updateOnlineBookingBookingItems(applyWalkingPreferredStaff(walkingItems, staff))
            );
        },
        [walkingItems]
    );

    const submitHandler = React.useCallback(() => {
        dispatch(
            updateOnlineBookingBookingItems(applyWalkingStaffOnSubmit(walkingItems, preferredStaff))
        );

        trackStaffSelected({
            marketplace_slug: marketplace.slug,
            business_area: getBusinessArea(walkingItems),
            staff_preference: preferredStaff ? 'specific' : 'no_preference'
        });

        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    }, [walkingItems, preferredStaff, marketplace.slug]);

    const view = deriveWalkingStaffStepView({
        allowSelectStaff,
        isLoading,
        fetchError,
        derivation,
        preferredStaffId: preferredStaff?.id
    });

    if (!allowSelectStaff) {
        return null;
    }

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h1 underlined w-full mb-6">Select staff</h1>

                {fetchError && (
                    <Alert
                        variant="error"
                        icon={faInfoCircle}
                        text="Something went wrong. Please go back and try again."
                    />
                )}

                {view.showUnavailableLine && (
                    <NoAvailability description={copy.staffAllUnavailable} />
                )}

                {view.showCards && (
                    <div className={staffClasses.root}>
                        <RadioAvatarCard
                            icon={faShuffle}
                            label="No preference"
                            loading={isLoading}
                            checked={preferredStaff === undefined}
                            description={walkingStaffCardDescription(derivation.noPreference)}
                            onChange={() => changePreferredStaffHandler(undefined)}
                        />

                        {availableStaffs.map(staff => {
                            const card = derivation.staff.find(item => item.staffId === staff.id);

                            if (!isLoading && !card) {
                                return null;
                            }

                            return (
                                <StaffRadioCard
                                    key={staff.id}
                                    staff={staff}
                                    dateFormat={WALKING_STAFF_DATE_FORMAT}
                                    loading={isLoading}
                                    description={walkingStaffCardDescription(
                                        card ?? { selectable: false, nextAvailableDate: null }
                                    )}
                                    selected={preferredStaff?.id === staff.id}
                                    onSelected={() => changePreferredStaffHandler(staff)}
                                />
                            );
                        })}
                    </div>
                )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter disabled={view.footerDisabled} onSubmit={submitHandler} />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
});

OnlineBookingWalkingStaffListStep.displayName = 'OnlineBookingWalkingStaffListStep';
